import { COURSE_DATA, COURSE_DATA_META } from '../data/courseData.js'
import { COURSE_REGISTRATION_SCHEDULE } from '../data/courseRegistrationSchedule.js'
import { getCourseScheduleMode, normalizeCourseIsOnline } from '../utils/courseScheduleMode.js'

const DEFAULT_API_URL = 'https://suguide-suwon-course-sync.choys99999.workers.dev/v1/courses'
const RUNTIME_CATALOG_STORAGE_KEY_PREFIX = 'suguide:runtime-catalog:v1'
const LAST_KNOWN_GOOD_KEY_PREFIX = 'suguide:last-known-good:v1'

const CATEGORY_MAP = {
  전선: '전공선택',
  전취: '전공필수',
  전핵: '전공필수',
  기교: '기초교양',
  선교: '교양선택',
  소교: '교양선택',
  중핵: '핵심교양',
  교직: '교직',
  선수: '선수과목',
  RT: '일반선택',
}

export function resolveCatalogTerm(now = new Date()) {
  const scheduleSemesterKey = COURSE_REGISTRATION_SCHEDULE?.semesterKey
  if (scheduleSemesterKey && typeof scheduleSemesterKey === 'string') {
    const [yearStr, semStr] = scheduleSemesterKey.split('-')
    const year = Number(yearStr)
    const semester = Number(semStr)
    if (Number.isInteger(year) && (semester === 1 || semester === 2)) {
      return { year, semester, key: `${year}-${semester}` }
    }
  }
  const month = now.getMonth() + 1
  const year = now.getFullYear()
  const semester = month >= 8 ? 2 : 1
  return { year, semester, key: `${year}-${semester}` }
}

function stableId(value) {
  if (value == null) return ''
  return String(value).trim().replace(/^\d{4}-[12]-/, '')
}

function normalizeMeeting(meeting = {}) {
  const periods = Array.isArray(meeting.periods)
    ? [...new Set(meeting.periods.map(Number).filter(Number.isInteger))].sort((a, b) => a - b)
    : (meeting.startPeriod != null
        ? Array.from(
            { length: Math.max(1, (Number(meeting.endPeriod ?? meeting.startPeriod) - Number(meeting.startPeriod) + 1)) },
            (_, i) => Number(meeting.startPeriod) + i
          )
        : [])
  const startPeriod = periods.length ? periods[0] : (meeting.startPeriod != null ? Number(meeting.startPeriod) : null)
  const endPeriod = periods.length ? periods.at(-1) : (meeting.endPeriod != null ? Number(meeting.endPeriod) : startPeriod)
  const classroom = String(meeting.classroom || meeting.room || '').trim()
  const days = Array.isArray(meeting.days)
    ? meeting.days.map(String).filter(Boolean)
    : (meeting.day ? [String(meeting.day)] : [])
  return {
    day: days[0] ?? '',
    days,
    periods,
    startPeriod,
    endPeriod,
    classroom,
    room: classroom,
  }
}

export function normalizeRuntimeCatalogCourse(course = {}) {
  const rawMeetings = Array.isArray(course.meetings) && course.meetings.length > 0
    ? course.meetings
    : Array.isArray(course.schedule) && course.schedule.length > 0
      ? course.schedule
      : []
  const meetings = rawMeetings.map(normalizeMeeting).filter(m => m.days.length > 0 || m.startPeriod != null || m.classroom)
  const fallbackDays = (Array.isArray(course.days) ? course.days : (course.day ? [course.day] : [])).map(String).filter(Boolean)
  const days = meetings.length > 0 ? [...new Set(meetings.flatMap(m => m.days))] : fallbackDays
  const first = meetings[0] ?? {}
  const startPeriod = first.startPeriod ?? (course.startPeriod != null ? Number(course.startPeriod) : null)
  const endPeriod = first.endPeriod ?? (course.endPeriod != null ? Number(course.endPeriod) : startPeriod)
  const classroom = String(first.classroom || course.classroom || course.room || '').trim()
  const credits = Number(course.credits ?? course.credit ?? 0)
  const capacity = course.capacity == null ? null : Number(course.capacity)
  const enrolled = course.enrolled == null ? null : Number(course.enrolled)
  const targetGrade = Number.isInteger(Number(course.targetGrade)) ? Number(course.targetGrade) : null
  const id = String(course.id ?? '').trim()
  const name = String(course.name ?? course.courseName ?? '').trim()
  const parsedId = parseCanonicalCourseIdentity(id)
  const subjectKey = String(course.subjectKey ?? course.courseCode ?? parsedId?.courseCode ?? '').trim()
  const courseCode = String(course.courseCode ?? course.subjectKey ?? parsedId?.courseCode ?? '').trim()
  const division = String(course.division ?? course.section ?? parsedId?.section ?? '').trim()
  const departmentCode = String(course.departmentCode ?? course.departmentId ?? parsedId?.departmentCode ?? '').trim()

  const normalized = {
    ...course,
    id,
    liveCourseId: id,
    subjectKey,
    courseCode,
    name,
    courseName: name,
    division,
    department: String(course.department ?? '').trim(),
    departmentCode,
    category: CATEGORY_MAP[course.category] ?? course.category ?? '',
    sourceCategory: String(course.sourceCategory ?? course.category ?? '').trim(),
    classType: String(course.classType ?? '').trim(),
    credit: credits,
    credits,
    professor: String(course.professor ?? '').trim(),
    days,
    day: days[0] ?? '',
    startPeriod,
    endPeriod,
    periods: first.periods ?? (startPeriod != null ? [startPeriod] : []),
    room: classroom,
    classroom,
    meetings: meetings.length > 0
      ? meetings
      : (days.length || startPeriod != null || classroom
          ? [normalizeMeeting({ day: days[0] ?? '', days, startPeriod, endPeriod, classroom })]
          : []),
    isOnline: normalizeCourseIsOnline(course),
    capacity: Number.isFinite(capacity) && capacity > 0 ? capacity : null,
    enrolled: Number.isFinite(enrolled) && enrolled >= 0 ? enrolled : null,
    targetGrade,
    scheduleMode: getCourseScheduleMode({ isOnline: normalizeCourseIsOnline(course) }),
  }
  normalized.searchText = [
    normalized.name, normalized.professor, normalized.room, normalized.classroom,
    normalized.department, normalized.category, normalized.division, normalized.courseCode,
  ].join(' ').toLowerCase()
  return normalized
}

function computeCatalogHash(courses = []) {
  const simplified = courses.map(c => `${c.id}|${c.name}|${c.professor}|${c.classroom}|${c.credit}|${c.days?.join(',')}|${c.startPeriod}-${c.endPeriod}`)
  let hash = 0
  for (const row of simplified) {
    for (let i = 0; i < row.length; i++) {
      hash = ((hash << 5) - hash) + row.charCodeAt(i)
      hash |= 0
    }
  }
  return `h_${Math.abs(hash).toString(16)}_${courses.length}`
}

function validateCatalogIntegrity(courses) {
  if (!Array.isArray(courses) || courses.length === 0) {
    return { valid: false, reason: 'Empty catalog array' }
  }
  const seenIds = new Set()
  let parserFailures = 0
  for (const c of courses) {
    const name = c?.name ?? c?.courseName
    if (!c?.id || !name) {
      parserFailures++
      continue
    }
    const sid = stableId(c.id)
    if (seenIds.has(sid)) {
      return { valid: false, reason: `Duplicate stable ID in catalog: ${sid}` }
    }
    seenIds.add(sid)
  }
  if (parserFailures > 0) {
    return { valid: false, reason: `${parserFailures} courses failed basic schema validation` }
  }
  return { valid: true, count: courses.length }
}

let activeCatalog = null
let activeCatalogIndex = null
let activeMeta = null
let syncInFlight = null

export function parseCanonicalCourseIdentity(courseOrId) {
  if (!courseOrId) return null
  if (typeof courseOrId === 'string') {
    const cleaned = String(courseOrId).trim().replace(/^\d{4}-[12]-/, '')
    const parts = cleaned.split('-')
    if (parts.length >= 2) {
      const courseCode = parts[0].trim()
      const section = parts[1].trim()
      const departmentCode = parts[2]?.trim() || ''
      return {
        courseCode,
        section,
        departmentCode: departmentCode === 'all' ? '' : departmentCode,
        stableId: cleaned,
      }
    }
    return {
      courseCode: cleaned,
      section: '',
      departmentCode: '',
      stableId: cleaned,
    }
  }

  const rawId = String(courseOrId.id ?? courseOrId.courseId ?? courseOrId.sectionId ?? courseOrId.liveCourseId ?? '').trim()
  const code = String(courseOrId.subjectKey ?? courseOrId.courseCode ?? courseOrId.subjectCode ?? courseOrId.subjtCd ?? '').trim()
  const section = String(courseOrId.division ?? courseOrId.section ?? courseOrId.diclNo ?? '').trim()
  const departmentCode = String(courseOrId.departmentCode ?? courseOrId.departmentId ?? courseOrId.estbDpmjCd ?? '').trim()

  if (code && section) {
    const baseSid = departmentCode ? `${code}-${section}-${departmentCode}` : `${code}-${section}`
    return {
      courseCode: code,
      section,
      departmentCode,
      stableId: rawId ? stableId(rawId) : baseSid,
    }
  }
  if (rawId) {
    return parseCanonicalCourseIdentity(rawId)
  }
  return null
}

function buildCatalogIndex(courses = []) {
  const map = new Map()
  for (const course of courses) {
    const rawId = String(course?.id ?? '').trim()
    const sid = stableId(rawId)
    if (rawId) map.set(rawId, course)
    if (sid) map.set(sid, course)
    const code = String(course.courseCode ?? course.subjectKey ?? '').trim()
    const div = String(course.division ?? course.section ?? '').trim()
    const dept = String(course.departmentCode ?? course.departmentId ?? '').trim()
    if (code && div) {
      if (dept) {
        map.set(`${code}-${div}-${dept}`, course)
      }
      if (!map.has(`${code}-${div}`)) {
        map.set(`${code}-${div}`, course)
      }
    }
  }
  return map
}

function getStorage(storage) {
  if (storage) return storage
  if (typeof localStorage === 'undefined') return null
  return localStorage
}

export function resetRuntimeCatalogForTesting() {
  activeCatalog = null
  activeCatalogIndex = null
  activeMeta = null
  syncInFlight = null
}

export function initRuntimeCourseCatalog({ storage, now = new Date() } = {}) {
  if (activeCatalog && activeCatalogIndex) return activeCatalog
  const term = resolveCatalogTerm(now)
  const store = getStorage(storage)
  
  if (store) {
    try {
      const cachedRaw = store.getItem(`${RUNTIME_CATALOG_STORAGE_KEY_PREFIX}:${term.key}`)
      if (cachedRaw) {
        const parsed = JSON.parse(cachedRaw)
        const validation = validateCatalogIntegrity(parsed?.courses)
        if (validation.valid) {
          activeCatalog = parsed.courses.map(normalizeRuntimeCatalogCourse)
          activeCatalogIndex = buildCatalogIndex(activeCatalog)
          activeMeta = {
            schemaVersion: parsed.schemaVersion ?? 1,
            semester: term.key,
            version: parsed.version ?? parsed.hash ?? 'cached',
            count: activeCatalog.length,
            hash: parsed.hash ?? computeCatalogHash(activeCatalog),
            publishedAt: parsed.publishedAt ?? parsed.cachedAt ?? new Date().toISOString(),
            source: 'live-cache',
          }
          return activeCatalog
        }
      }
    } catch {
      // Corrupt cache recovery
    }
  }

  // Bundled fallback
  const fallbackList = COURSE_DATA.map(normalizeRuntimeCatalogCourse)
  activeCatalog = fallbackList
  activeCatalogIndex = buildCatalogIndex(activeCatalog)
  activeMeta = {
    schemaVersion: 1,
    // 번들 데이터는 자기 학기다. 일정상 학기(term)가 넘어가도 새 학기 공식 데이터로 오인하지 않는다.
    semester: COURSE_DATA_META?.semesterKey ?? term.key,
    version: 'bundled-fallback',
    count: fallbackList.length,
    hash: computeCatalogHash(fallbackList),
    publishedAt: null,
    source: 'bundled-fallback',
  }
  return activeCatalog
}

export function getRuntimeCourseCatalog() {
  if (!activeCatalog) {
    initRuntimeCourseCatalog()
  }
  return activeCatalog
}

export function findRuntimeCatalogCourse(courseOrId, catalog = null) {
  if (!courseOrId) return null
  if (catalog && Array.isArray(catalog)) {
    const parsed = parseCanonicalCourseIdentity(courseOrId)
    const rawId = typeof courseOrId === 'string' ? courseOrId.trim() : String(courseOrId.id ?? courseOrId.courseId ?? courseOrId.sectionId ?? courseOrId.liveCourseId ?? '').trim()
    const sid = rawId ? stableId(rawId) : ''

    // 1. Direct ID / stable ID match
    for (const candidate of catalog) {
      const candRawId = String(candidate?.id ?? '').trim()
      const candSid = stableId(candRawId)
      if ((rawId && candRawId === rawId) || (sid && candSid === sid)) {
        return candidate
      }
    }

    // 2. Canonical (courseCode + section + departmentCode) match
    if (parsed?.courseCode && parsed?.section) {
      if (parsed.departmentCode) {
        for (const candidate of catalog) {
          const candCode = String(candidate.courseCode ?? candidate.subjectKey ?? '').trim()
          const candSec = String(candidate.division ?? candidate.section ?? '').trim()
          const candDept = String(candidate.departmentCode ?? candidate.departmentId ?? '').trim()
          if (candCode === parsed.courseCode && candSec === parsed.section && candDept === parsed.departmentCode) {
            return candidate
          }
        }
        // A full identity explicitly names its department. Falling through to
        // the department-less matcher would turn a contradictory identity into
        // a different official course.
        return null
      }

      // 3. Canonical (courseCode + section) match. A department-less legacy ID
      // must not silently attach to the first of multiple different courses.
      const candidates = []
      for (const candidate of catalog) {
        const candCode = String(candidate.courseCode ?? candidate.subjectKey ?? '').trim()
        const candSec = String(candidate.division ?? candidate.section ?? '').trim()
        if (candCode === parsed.courseCode && candSec === parsed.section) {
          candidates.push(candidate)
        }
      }
      if (candidates.length === 1) return candidates[0]
      if (candidates.length > 1) {
        if (typeof courseOrId === 'object') {
          const storedName = String(courseOrId.name ?? courseOrId.courseName ?? '').trim()
          const storedDepartment = String(courseOrId.department ?? '').trim()
          const identified = candidates.filter(candidate =>
            (!storedName || String(candidate.name ?? candidate.courseName ?? '').trim() === storedName)
            && (!storedDepartment || String(candidate.department ?? '').trim() === storedDepartment)
          )
          if (identified.length === 1) return identified[0]
        }
        const factSignature = candidate => JSON.stringify({
          name: candidate.name ?? candidate.courseName ?? '',
          professor: candidate.professor ?? '',
          credit: Number(candidate.credit ?? candidate.credits ?? 0),
          meetings: candidate.meetings ?? [],
        })
        if (new Set(candidates.map(factSignature)).size === 1) return candidates[0]
      }
    }

    return null
  }

  if (!activeCatalogIndex) {
    initRuntimeCourseCatalog()
  }
  return findRuntimeCatalogCourse(courseOrId, activeCatalog)
}

export function getRuntimeCatalogMeta() {
  if (!activeMeta) {
    initRuntimeCourseCatalog()
  }
  return activeMeta
}

export function setRuntimeCourseCatalogManually(courses, meta = {}, { storage, preserveLkg = true } = {}) {
  const validation = validateCatalogIntegrity(courses)
  if (!validation.valid) {
    throw new Error(`Invalid catalog payload: ${validation.reason}`)
  }
  const normalized = courses.map(normalizeRuntimeCatalogCourse)
  const previousGoodCatalog = activeCatalog
  const previousGoodMeta = activeMeta
  activeCatalog = normalized
  activeCatalogIndex = buildCatalogIndex(normalized)
  const term = resolveCatalogTerm()
  activeMeta = {
    schemaVersion: meta.schemaVersion ?? 1,
    semester: meta.semester ?? term.key,
    version: meta.version ?? meta.hash ?? 'manual',
    count: normalized.length,
    hash: meta.hash ?? computeCatalogHash(normalized),
    publishedAt: meta.publishedAt ?? new Date().toISOString(),
    source: meta.source ?? 'manual',
  }
  const store = getStorage(storage)
  if (store) {
    try {
      const existingLkg = store.getItem(`${LAST_KNOWN_GOOD_KEY_PREFIX}:${term.key}`)
      if (preserveLkg && previousGoodCatalog && previousGoodCatalog.length > 0) {
        store.setItem(`${LAST_KNOWN_GOOD_KEY_PREFIX}:${term.key}`, JSON.stringify({
          semester: previousGoodMeta?.semester,
          version: previousGoodMeta?.version ?? 'lkg-previous',
          hash: previousGoodMeta?.hash ?? computeCatalogHash(previousGoodCatalog),
          count: previousGoodCatalog.length,
          courses: previousGoodCatalog,
        }))
      } else if (!existingLkg) {
        store.setItem(`${LAST_KNOWN_GOOD_KEY_PREFIX}:${term.key}`, JSON.stringify({
          semester: activeMeta.semester,
          version: activeMeta.version,
          hash: activeMeta.hash,
          count: activeMeta.count,
          courses: normalized,
        }))
      }
      store.setItem(`${RUNTIME_CATALOG_STORAGE_KEY_PREFIX}:${term.key}`, JSON.stringify({
        schemaVersion: activeMeta.schemaVersion,
        semester: activeMeta.semester,
        version: activeMeta.version,
        count: activeMeta.count,
        hash: activeMeta.hash,
        publishedAt: activeMeta.publishedAt,
        cachedAt: new Date().toISOString(),
        courses: normalized,
      }))
    } catch {}
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('suguide:runtime-catalog-updated', { detail: activeMeta }))
  }
  return activeMeta
}

export async function syncRuntimeCourseCatalog({
  fetchImpl = globalThis.fetch,
  apiUrl = DEFAULT_API_URL,
  storage,
  now = new Date(),
  force = false,
} = {}) {
  if (syncInFlight) return syncInFlight
  const term = resolveCatalogTerm(now)
  initRuntimeCourseCatalog({ storage, now })

  const request = (async () => {
    try {
      const url = new URL(apiUrl)
      url.searchParams.set('year', String(term.year))
      url.searchParams.set('semester', String(term.semester))
      const metaUrl = new URL(`${url.pathname.replace(/\/$/, '')}/meta`, url)
      metaUrl.search = url.search

      const currentHash = activeMeta?.hash
      const metaHeaders = (!force && currentHash) ? { 'If-None-Match': `"${currentHash}"` } : {}
      
      const metaResponse = await fetchImpl(metaUrl, {
        cache: 'no-store',
        headers: metaHeaders,
      })

      if (metaResponse.status === 304 && activeCatalog?.length > 0) {
        return { status: 'unchanged', meta: activeMeta }
      }
      if (!metaResponse.ok) {
        throw new Error(`Meta fetch failed with status ${metaResponse.status}`)
      }
      const metaData = await metaResponse.json()
      if (!metaData.hash || !Number.isFinite(Number(metaData.count)) || Number(metaData.count) <= 0) {
        throw new Error('Invalid meta payload from worker')
      }

      // Anomaly detection: count drop check (>30% drop without explicit override flag)
      if (!force && activeCatalog && activeCatalog.length > 500 && metaData.count < activeCatalog.length * 0.7) {
        console.warn(`[CatalogSync] Course count dropped anomalously (${activeCatalog.length} -> ${metaData.count}). Holding update.`)
        return { status: 'hold', reason: 'anomaly_count_drop', meta: activeMeta }
      }

      if (!force && currentHash === metaData.hash && activeMeta?.source === 'live-cache') {
        return { status: 'unchanged', meta: activeMeta }
      }

      // Download full snapshot
      const fullResponse = await fetchImpl(url, { cache: 'no-store' })
      if (!fullResponse.ok) {
        throw new Error(`Catalog full fetch failed with status ${fullResponse.status}`)
      }
      const payload = await fullResponse.json()
      const rawCourses = Array.isArray(payload.courses) ? payload.courses : (Array.isArray(payload) ? payload : [])
      
      const validation = validateCatalogIntegrity(rawCourses)
      if (!validation.valid) {
        throw new Error(`Catalog integrity validation failed: ${validation.reason}`)
      }

      const normalizedCourses = rawCourses.map(normalizeRuntimeCatalogCourse)
      const computedHash = payload.hash ?? metaData.hash ?? computeCatalogHash(normalizedCourses)
      
      setRuntimeCourseCatalogManually(normalizedCourses, {
        schemaVersion: payload.schemaVersion ?? metaData.schemaVersion ?? 1,
        semester: term.key,
        version: payload.version ?? metaData.version ?? computedHash,
        hash: computedHash,
        publishedAt: payload.publishedAt ?? metaData.updatedAt ?? new Date().toISOString(),
        source: 'live-cache',
      }, { storage, preserveLkg: true })

      return { status: 'updated', meta: activeMeta }
    } catch (error) {
      // In case of error, last-known-good / bundled fallback remains untouched
      return { status: 'failed', error: error.message, meta: activeMeta }
    } finally {
      if (syncInFlight === request) {
        syncInFlight = null
      }
    }
  })()

  syncInFlight = request
  return request
}

export function rollbackRuntimeCourseCatalog({ storage, now = new Date() } = {}) {
  const term = resolveCatalogTerm(now)
  const store = getStorage(storage)
  if (!store) return false
  try {
    const lkgRaw = store.getItem(`${LAST_KNOWN_GOOD_KEY_PREFIX}:${term.key}`)
    if (!lkgRaw) return false
    const lkg = JSON.parse(lkgRaw)
    if (!validateCatalogIntegrity(lkg?.courses).valid) return false
    return setRuntimeCourseCatalogManually(lkg.courses, { ...lkg, source: 'rollback-lkg' }, { storage, preserveLkg: false })
  } catch {
    return false
  }
}
